import { useState } from 'react';
import {
  ALERT_TYPE_LABEL,
  UserRole,
  alertTypeSchema,
  type AlertSeverityValue,
  type AlertTypeValue,
} from '@teentack/shared';
import { useAuth } from '@/auth/use-auth';
import { useParentOverview } from '@/features/parent/use-parent-overview';
import { useMyAlerts, useMyReports, useRaiseAlert } from '@/features/safety/use-safety';
import { EmptyState } from '@/components/ui/Feedback';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { SelectInput } from '@/components/ui/Input';
import { ErrorNote, InfoNote } from '@/components/Form';
import { ShieldCheckIcon } from '@/components/ui/Icons';
import { relativeTime } from '@/lib/relative-time';
import { presentApiError } from '@/lib/form-errors';

/**
 * Getting help. [Parent step 17, Job Poster step 25]
 *
 * Built to be usable by someone who is worried and in a hurry: the alarm is the
 * first thing on the page, it takes two taps, and it works even from an account
 * the rest of the marketplace has shut out.
 */

const SEVERITY_TONES: Record<AlertSeverityValue, BadgeTone> = {
  LOW: 'neutral',
  MEDIUM: 'brand',
  HIGH: 'sun',
  CRITICAL: 'sun',
};

const ALERT_TYPES = alertTypeSchema.options;

export function SafetyPage() {
  const { user } = useAuth();
  const isParent = user?.role === UserRole.PARENT;

  const [alertType, setAlertType] = useState<AlertTypeValue>('FEELING_UNSAFE');
  const [aboutTeenId, setAboutTeenId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const overview = useParentOverview(isParent);
  const alerts = useMyAlerts({ pageSize: 20 });
  const reports = useMyReports({ pageSize: 20 });
  const raise = useRaiseAlert();

  const teens = (overview.data?.teens ?? []).filter((teen) => teen.verified);

  async function send(): Promise<void> {
    setError(null);
    try {
      await raise.mutateAsync({
        alertType,
        // Anything raised from this screen is urgent by default; grading it
        // down is a moderation decision, not one to ask a worried person for.
        severity: 'HIGH',
        ...(aboutTeenId ? { aboutTeenId } : {}),
      });
      setSent(true);
    } catch (caught) {
      setError(presentApiError(caught, 'Could not send that. Try again.').message);
    }
  }

  return (
    <main className="container-page py-10">
      <div className="mx-auto max-w-3xl">
        <header className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            Help and safety
          </p>
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            Get help
          </h1>
          <p className="mt-2 max-w-prose text-sm leading-relaxed text-slate-500">
            If something feels wrong, tell us. You do not need to be sure, and you do not need to
            explain it first.
          </p>
        </header>

        {/* The alarm comes first, before any history. */}
        <section className="mb-10 rounded-2xl border border-sun-300 bg-sun-50 p-6">
          <h2 className="text-base font-bold text-sun-900">Raise an alert</h2>
          <p className="mt-1 text-sm leading-relaxed text-sun-800">
            This goes straight to our team. If someone is in immediate danger, call your local
            emergency number first.
          </p>

          {sent ? (
            <div className="mt-4">
              <InfoNote>
                Alert sent. Our team has it, and we will be in touch. You can raise another if
                anything changes.
              </InfoNote>
              <Button className="mt-3" variant="outline" onClick={() => setSent(false)}>
                Raise another
              </Button>
            </div>
          ) : (
            <>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <SelectInput
                  label="What is happening?"
                  value={alertType}
                  onChange={(event) => setAlertType(event.target.value as AlertTypeValue)}
                >
                  {ALERT_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {ALERT_TYPE_LABEL[value]}
                    </option>
                  ))}
                </SelectInput>

                {isParent && teens.length > 0 && (
                  <SelectInput
                    label="Who is this about?"
                    value={aboutTeenId}
                    onChange={(event) => setAboutTeenId(event.target.value)}
                    optional
                  >
                    <option value="">Me</option>
                    {teens.map((teen) => (
                      <option key={teen.id} value={teen.id}>
                        {teen.name}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </div>

              {error && (
                <div className="mt-3">
                  <ErrorNote message={error} />
                </div>
              )}

              <Button
                className="mt-4"
                variant="danger"
                disabled={raise.isPending}
                onClick={() => void send()}
              >
                {raise.isPending ? 'Sending…' : 'Send alert'}
              </Button>
            </>
          )}
        </section>

        {/* History */}
        <section className="mb-10">
          <h2 className="mb-4 text-base font-bold text-ink">Alerts you have raised</h2>

          {alerts.data && alerts.data.items.length === 0 && (
            <EmptyState icon={<ShieldCheckIcon className="size-6" />} title="No alerts">
              Nothing has been raised. That is the way it should be.
            </EmptyState>
          )}

          {alerts.data && alerts.data.items.length > 0 && (
            <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white shadow-soft">
              {alerts.data.items.map((alert) => (
                <li
                  key={alert.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-ink">
                      {ALERT_TYPE_LABEL[alert.alertType as AlertTypeValue] ?? alert.alertType}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {alert.jobTitle ? `${alert.jobTitle} · ` : ''}
                      {relativeTime(alert.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={SEVERITY_TONES[alert.severity]}>{alert.severity}</Badge>
                    <Badge tone={alert.status === 'OPEN' ? 'brand' : 'safe'}>
                      {alert.status.charAt(0) + alert.status.slice(1).toLowerCase()}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2 className="mb-4 text-base font-bold text-ink">People you have reported</h2>
          <p className="mb-4 text-sm text-slate-500">
            You can report someone from their name on any application, bid or conversation.
          </p>

          {reports.data && reports.data.items.length === 0 && (
            <EmptyState icon={<ShieldCheckIcon className="size-6" />} title="No reports">
              You have not reported anyone.
            </EmptyState>
          )}

          {reports.data && reports.data.items.length > 0 && (
            <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white shadow-soft">
              {reports.data.items.map((report) => (
                <li key={report.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-bold text-ink">{report.reportedName}</p>
                    <Badge tone={report.status === 'OPEN' ? 'brand' : 'safe'}>
                      {report.status.replace(/_/g, ' ').toLowerCase()}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{report.reason}</p>
                  <p className="mt-1 text-xs text-slate-400">
                    {report.jobTitle ? `${report.jobTitle} · ` : ''}
                    {relativeTime(report.createdAt)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
